<template>
	<div>
		<DashboardHeader
			:title="$route.name"
			:buttonItems="buttonItems"
			@click="handleHeaderClick"
		/>
		<div class="container-fluid mt--6">
			<b-overlay :show="isLoading" rounded="sm" no-wrap />
			<Card no-body>
				<b-row align-v="center" slot="header">
					<b-col cols="8">
						<h3 class="mb-0">Registrations</h3>
					</b-col>
					<b-col cols="4" class="text-right">
						<h3 class="mb-0">
							{{ filteredCount }}{{ search ? ' of ' + allOrders.length : '' }}
							{{ allOrders.length === 1 ? 'Order' : 'Orders' }}
						</h3>
					</b-col>
				</b-row>
				<!-- Search UI matches Members/Pending Members tables: BaseInput
				     with a search icon, between card header and the table. -->
				<BaseInput
					id="filter-input"
					v-model="search"
					type="search"
					class="mb-0 mt-3 mx-3"
					placeholder="Type to Search"
					prependIcon="fa fa-search"
				></BaseInput>
				<b-table
					:items="allOrders"
					:fields="tableFields"
					:per-page="perPage"
					:current-page="currentPage"
					:filter="search"
					:filter-function="filterOrder"
					hover
					responsive
					sort-by="createdAtMillis"
					:sort-desc="true"
					@row-clicked="onRowClicked"
					@filtered="onFiltered"
					tbody-tr-class="cursor-pointer"
				>
					<template #cell(purchaser)="row">
						<strong>
							{{ row.item.buyer.firstName }} {{ row.item.buyer.lastName }}
						</strong>
					</template>
					<template #cell(email)="row">
						<a :href="`mailto:${row.item.buyer.email}`" @click.stop>
							{{ row.item.buyer.email }}
						</a>
					</template>
					<template #cell(attendees)="row">
						{{ row.item.attendees ? row.item.attendees.length : 0 }}
					</template>
					<template #cell(totalCents)="row">
						{{ amountToDollars(row.item.totalCents) }}
					</template>
					<template #cell(status)="row">
						<b-badge :variant="statusVariant(row.item.status)">
							{{ row.item.status }}
						</b-badge>
						<small v-if="row.item.isTestMode" class="text-warning ml-1">TEST</small>
					</template>
					<template #cell(actions)="row">
						<!-- dropleft + no-caret matches the Members/Pending Members
						     dropdown so the menu opens away from the right edge of
						     the card and doesn't get clipped by overflow. -->
						<b-dropdown
							id="actions"
							variant="null"
							dropleft
							no-caret
							class="m-0 p-0"
							@click.native.stop
						>
							<template #button-content>
								<i class="fas fa-ellipsis-v"></i>
							</template>
							<b-dropdown-item @click.stop="viewClicked(row.item)">
								View details
							</b-dropdown-item>
							<b-dropdown-item @click.stop="copyEmail(row.item.buyer.email)">
								Copy purchaser email
							</b-dropdown-item>
							<b-dropdown-item
								v-if="row.item.stripeReceiptUrl"
								:href="row.item.stripeReceiptUrl"
								target="_blank"
							>
								Open Stripe receipt
							</b-dropdown-item>
							<b-dropdown-item @click.stop="copyOrderId(row.item.orderId)">
								Copy order ID
							</b-dropdown-item>
						</b-dropdown>
					</template>
				</b-table>
				<b-pagination
					v-if="allOrders.length > perPage"
					v-model="currentPage"
					:total-rows="allOrders.length"
					:per-page="perPage"
					align="center"
					class="my-3"
				/>
			</Card>
		</div>
	</div>
</template>
<script>
import { collection, getDocs, query, orderBy } from '@firebase/firestore';
import ExcelJS from 'exceljs';
import { mapActions, mapGetters } from 'vuex';
import { getOrganizationFirestore } from '../../../resources/organizationFirebase';
import { DashboardChildren } from '../../../routes/routesNames';
import Card from '../../../components/Cards/Card.vue';
import BaseButton from '../../../components/BaseButton.vue';
import BaseInput from '../../../components/Inputs/BaseInput.vue';
import DashboardHeader from '../HeaderFooterComponents/DashboardHeader.vue';

export default {
	name: 'Registrations',
	components: { Card, BaseButton, BaseInput, DashboardHeader },
	computed: {
		...mapGetters({ organization: 'getOrganization' }),
	},
	data() {
		return {
			isLoading: true,
			allOrders: [],
			perPage: 25,
			currentPage: 1,
			search: '',
			// Tracks rows after b-table filtering so the header count reflects
			// "<filtered> of <total>" when a search query is active.
			filteredCount: 0,
			buttonItems: [
				{ index: 0, title: 'Export CSV', type: 'primary', icon: 'fas fa-download' },
			],
			// Column order: Purchaser first (most-scannable identity), then date,
			// attendee count, total, status, and actions menu at the far right.
			// createdAtMillis is the sort key; the formatter renders the
			// human-readable timestamp in the cell.
			tableFields: [
				{
					key: 'purchaser',
					label: 'Purchaser',
					sortable: true,
					sortByFormatted: (value, key, item) =>
						`${item.buyer.firstName} ${item.buyer.lastName}`.trim().toLowerCase(),
				},
				{
					key: 'email',
					label: 'Email',
					sortable: true,
					sortByFormatted: (value, key, item) =>
						(item.buyer.email || '').toLowerCase(),
				},
				{
					key: 'createdAtMillis',
					label: 'Date',
					sortable: true,
					formatter: (value, key, item) => this.formatTimestamp(item.createdAt),
				},
				{ key: 'attendees', label: 'Attendees', sortable: true },
				{ key: 'totalCents', label: 'Total', sortable: true },
				{ key: 'status', label: 'Status', sortable: true },
				{ key: 'actions', label: '', class: 'text-right' },
			],
		};
	},
	methods: {
		...mapActions(['showNotification']),
		amountToDollars(cents) {
			if (cents == null) return '$0.00';
			return Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
			}).format(cents / 100);
		},
		formatTimestamp(value) {
			if (!value) return '';
			const date = value.toDate ? value.toDate() : new Date(value);
			return date.toLocaleString();
		},
		formatTimestampIso(value) {
			if (!value) return '';
			const date = value.toDate ? value.toDate() : new Date(value);
			return date.toISOString();
		},
		statusVariant(status) {
			switch (status) {
				case 'paid':
					return 'success';
				case 'pending':
				case 'partial':
					return 'warning';
				case 'failed':
					return 'danger';
				default:
					return 'secondary';
			}
		},
		handleHeaderClick(button) {
			if (button.index === 0) this.exportCsv();
		},
		// Custom filter — matches the search term against purchaser name/email,
		// order ID, and any attendee email. Case-insensitive substring match.
		filterOrder(row, term) {
			if (!term) return true;
			const q = String(term).toLowerCase();
			const fields = [
				row.orderId,
				row.buyer && row.buyer.firstName,
				row.buyer && row.buyer.lastName,
				row.buyer && row.buyer.email,
				row.buyer && row.buyer.businessName,
			];
			if (Array.isArray(row.attendees)) {
				for (const a of row.attendees) {
					fields.push(a.firstName, a.lastName, a.email, a.businessName);
				}
			}
			return fields.some(
				(v) => v && String(v).toLowerCase().indexOf(q) !== -1
			);
		},
		onFiltered(filteredItems) {
			this.filteredCount = filteredItems.length;
			this.currentPage = 1;
		},
		// Whole-row click navigates to the registration detail view — same
		// pattern as Members → MemberView.
		onRowClicked(item) {
			this.viewClicked(item);
		},
		viewClicked(item) {
			this.$router.push({
				name: DashboardChildren.registrationView.name,
				query: { orderId: item.orderId },
			});
		},
		copyEmail(email) {
			if (!email) return;
			navigator.clipboard.writeText(email).then(
				() => this.showNotification({ type: 'success', message: `Copied ${email}` }),
				() => this.showNotification({ type: 'danger', message: 'Copy failed.' })
			);
		},
		copyOrderId(orderId) {
			if (!orderId) return;
			navigator.clipboard.writeText(orderId).then(
				() => this.showNotification({ type: 'success', message: 'Order ID copied' }),
				() => this.showNotification({ type: 'danger', message: 'Copy failed.' })
			);
		},
		getOrders() {
			this.isLoading = true;
			const firestore = getOrganizationFirestore(this.organization);
			const ordersRef = query(collection(firestore, 'orders'), orderBy('createdAt', 'desc'));
			getDocs(ordersRef)
				.then((snapshot) => {
					this.allOrders = snapshot.docs.map((doc) => {
						const data = doc.data();
						const ts = data.createdAt;
						let millis = 0;
						if (ts && typeof ts.toMillis === 'function') {
							millis = ts.toMillis();
						} else if (ts) {
							const d = new Date(ts);
							millis = isNaN(d.getTime()) ? 0 : d.getTime();
						}
						return Object.assign({}, data, { createdAtMillis: millis });
					});
					this.filteredCount = this.allOrders.length;
				})
				.catch((error) => {
					this.showNotification({ type: 'danger', message: error.message });
				})
				.finally(() => {
					this.isLoading = false;
				});
		},
		async exportCsv() {
			const workbook = new ExcelJS.Workbook();
			const sheet = workbook.addWorksheet('Registrations');
			sheet.columns = [
				{ header: 'Order ID', key: 'orderId', width: 24 },
				{ header: 'Date', key: 'date', width: 22 },
				{ header: 'Status', key: 'status', width: 10 },
				{ header: 'Purchaser Name', key: 'purchaserName', width: 24 },
				{ header: 'Purchaser Email', key: 'purchaserEmail', width: 30 },
				{ header: 'Purchaser Studio', key: 'purchaserStudio', width: 24 },
				{ header: 'Attendee Name', key: 'attendeeName', width: 24 },
				{ header: 'Attendee Email', key: 'attendeeEmail', width: 30 },
				{ header: 'Attendee Studio', key: 'attendeeStudio', width: 24 },
				{ header: 'Items', key: 'items', width: 50 },
				{ header: 'Subtotal', key: 'subtotal', width: 12 },
				{ header: 'Coupon', key: 'coupon', width: 14 },
				{ header: 'Fee', key: 'fee', width: 10 },
				{ header: 'Total', key: 'total', width: 12 },
				{ header: 'Test Mode', key: 'testMode', width: 10 },
			];
			this.allOrders.forEach((order) => {
				const itemSummary = (order.items || [])
					.map((i) => `${i.quantity}x ${i.productTitle}`)
					.join('; ');
				const attendeeList =
					order.attendees && order.attendees.length > 0
						? order.attendees
						: [{ firstName: '', lastName: '', email: '', businessName: '' }];
				attendeeList.forEach((a) => {
					sheet.addRow({
						orderId: order.orderId,
						date: this.formatTimestampIso(order.createdAt),
						status: order.status,
						purchaserName: `${order.buyer.firstName} ${order.buyer.lastName}`,
						purchaserEmail: order.buyer.email,
						purchaserStudio: order.buyer.businessName || '',
						attendeeName: `${a.firstName} ${a.lastName}`.trim(),
						attendeeEmail: a.email,
						attendeeStudio: a.businessName || '',
						items: itemSummary,
						subtotal: this.amountToDollars(order.subtotalCents),
						coupon: order.couponCode
							? `${order.couponCode} (-${this.amountToDollars(order.couponDiscountCents)})`
							: '',
						fee: this.amountToDollars(order.feeCents),
						total: this.amountToDollars(order.totalCents),
						testMode: order.isTestMode ? 'TEST' : '',
					});
				});
			});
			const buffer = await workbook.csv.writeBuffer();
			const blob = new Blob([buffer], { type: 'text/csv' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `registrations-${Date.now()}.csv`;
			link.click();
			setTimeout(() => URL.revokeObjectURL(url), 0);
		},
	},
	mounted() {
		this.getOrders();
	},
};
</script>
<style scoped>
.cursor-pointer {
	cursor: pointer;
}
</style>
